/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';

import { FilterNames, FilterRestService, PrinterBusinessService } from '@gh/builder-shared';
import {
  ConstructionScheduleRestService,
  ContactRestService,
  JobRestService,
  MaintenanceScheduleRestService,
  PreConstructionScheduleRestService,
  PrinterRestService,
  ReportRestService,
  SalesFolderRestService,
  SalesScheduleRestService,
  ScheduleTemplateRestService,
  ScheduleType,
  SystemRoleBit,
  systemRolesBitSet,
  UserAutocompleteDto,
  UserAutocompleteQuery,
  UserListItemDto,
  UserRestService,
} from '@gh/builder-shared-data';
import { DictionaryService, HelpID } from '@gh/common-shared';
import { AutocompleteItem, ID, ReportStatus } from '@gh/common-shared-data';
import {
  decorateDataSourceQuery,
  FilteredDataQuery,
  getSlicedData,
  SlicedDataSource,
  SortDirection,
} from '@gh/core-data';
import { DialogRouteSnapshot, EmptyDialogInput } from '@gh/core-ui';
import { GridLayoutService } from '@gh/core-ui-grid';
import { map$ } from '@gh/rx/operators';
import { compact } from 'lodash';
import { Observable } from 'rxjs';
import {
  ScheduleJobProgressFilter,
  scheduleJobProgressFilterToDto,
} from './schedule-details.model';
import {
  ConstructionScheduleFilter,
  ConstructionScheduleListItem,
  ContactVendorListItem,
  createSupervisorReportRequestDto,
  JobProgressReportConfig,
  MaintenanceScheduleFilter,
  MaintenanceScheduleListItem,
  PreConstructionScheduleFilter,
  PreConstructionScheduleListItem,
  SalesScheduleFilter,
  SalesScheduleListItem,
  SchedulesSupervisorPrintMode,
  ScheduleSupervisorListItem,
  ScheduleSupervisorReportFilter,
  scheduleSupervisorReportFilterToDto,
  ScheduleSupervisorReportTask,
  ScheduleTemplateListItem,
  ScheduleVendorReportRequest,
  SupervsiorReportRequestInput,
  vendorReportRequestToDto,
} from './schedule-list.model';
import { ScheduleDetailsDto } from '@gh/builder-shared-data';

export type JobScheduleCreateDialogConfig = {
  type: ScheduleType;
  helpId: HelpID;
};

export type ScheduleSupervisorsReportFilterDialogConfig = {
  isIncludeUnsentOrders: boolean;
};

@Injectable()
export class ScheduleListBusinessService {
  constructor(private constructionScheduleRest: ConstructionScheduleRestService,
              private preConstructionScheduleRest: PreConstructionScheduleRestService,
              private maintenanceScheduleRest: MaintenanceScheduleRestService,
              private salesScheduleRest: SalesScheduleRestService,
              private scheduleTemplateRest: ScheduleTemplateRestService,
              private jobRest: JobRestService,
              private filterService: FilterRestService,
              private reportRestService: ReportRestService,
              private salesFolderRest: SalesFolderRestService,
              private printerRest: PrinterRestService,
              private printerBusinessService: PrinterBusinessService,
              private userRest: UserRestService,
              private contactRest: ContactRestService,
              private dictionaries: DictionaryService,
              private reportRest: ReportRestService,
              private gridLayouts: GridLayoutService) {
  }

  initConstructionScheduleListView(): any {
    return {
      scheduleTypeDict: this.dictionaries.getByEnum(ScheduleType),
      gridLayout: this.gridLayouts.get('constructionSchedule'),
      init: this.constructionScheduleRest.initList(),
      printers: this.printerRest.findAll(),
      filter: this.filterService.get(FilterNames.constructionSchedule),
      supervisors: this.userRest.findSupervisors(),
    };
  }

  initPreConstructionScheduleListView(): any {
    return {
      scheduleTypeDict: this.dictionaries.getByEnum(ScheduleType),
      gridLayout: this.gridLayouts.get('preconstructionSchedule'),
      filter: this.filterService.get(FilterNames.preconstructionSchedule),
    };
  }

  initMaintenanceScheduleListView(): any {
    return {
      scheduleTypeDict: this.dictionaries.getByEnum(ScheduleType),
      gridLayout: this.gridLayouts.get('maintenanceSchedule'),
      filter: this.filterService.get(FilterNames.maintenanceSchedule),
      supervisors: this.userRest.findSupervisors(),
    };
  }

  initSalesScheduleListView(): any {
    return {
      scheduleTypeDict: this.dictionaries.getByEnum(ScheduleType),
      gridLayout: this.gridLayouts.get('salesSchedule'),
      filter: this.filterService.get(FilterNames.salesSchedule),
    };
  }

  initJobScheduleDialogView(route: DialogRouteSnapshot<JobScheduleCreateDialogConfig>): any {
    return {
      templates: this.findAllScheduleTemplates(route.input.type),
    };
  }

  initSalesScheduleDialogView(route: DialogRouteSnapshot<EmptyDialogInput>): any {
    return {
      templates: this.findAllScheduleTemplates(ScheduleType.Sales),
    };
  }

  initScheduleJobProgressFilterDialog(route: DialogRouteSnapshot<EmptyDialogInput>): any {
    return {
      reportFilterConfig: this.getJobProgressReportConfig(),
      printers: this.printerRest.findAll(),
    };
  }

  initScheduleVendorFilterDialog(): any {
    return {
      mailDeliveryExists: this.constructionScheduleRest.initVendorReport().pipe(
        map$(({ mailDeliveryExists }) => mailDeliveryExists)),
      printerList: this.printerRest.findAll(),
    };
  }

  initScheduleConstructionReportDialog(): any {
    return {
      printerList: this.printerRest.findAll(),
    };
  }

  removeConstructionSchedule(schedule: ConstructionScheduleListItem): Observable<void> {
    return this.constructionScheduleRest.remove(schedule.scheduleId, schedule.version!);
  }

  removePreConstructionSchedule(schedule: PreConstructionScheduleListItem): Observable<void> {
    return this.preConstructionScheduleRest.remove(schedule.scheduleId, schedule.version!);
  }

  removeMaintenanceSchedule(schedule: MaintenanceScheduleListItem): Observable<void> {
    return this.maintenanceScheduleRest.remove(schedule.scheduleId, schedule.version!);
  }

  removeSalesSchedule(schedule: SalesScheduleListItem): Observable<void> {
    return this.salesScheduleRest.remove(schedule.scheduleId, schedule.version!);
  }

  findAllConstructionSchedulesAsDataSource(filter: ConstructionScheduleFilter): SlicedDataSource<ConstructionScheduleListItem> {
    return this.constructionScheduleRest.findAllAsDataSource(filter);
  }

  findAllPreConstructionSchedulesAsDataSource(filter: PreConstructionScheduleFilter): SlicedDataSource<PreConstructionScheduleListItem> {
    return this.preConstructionScheduleRest.findAllAsDataSource(filter);
  }

  findAllMaintenanceSchedulesAsDataSource(filter: MaintenanceScheduleFilter): SlicedDataSource<MaintenanceScheduleListItem> {
    return this.maintenanceScheduleRest.findAllAsDataSource(filter);
  }

  findAllSalesSchedulesAsDataSource(filter: SalesScheduleFilter): SlicedDataSource<SalesScheduleListItem> {
    return this.salesScheduleRest.findAllAsDataSource(filter);
  }

  findAllScheduleTemplates(type: ScheduleType): Observable<ScheduleTemplateListItem[]> {
    return this.scheduleTemplateRest.findAllForAutocomplete(type);
  }

  findAllJobs(type: ScheduleType, query: FilteredDataQuery<string>): Observable<AutocompleteItem[]> {
    return this.jobRest.findAllAsAutocompleteDataSource({
        showDpp: type === ScheduleType.PreConstruction,
        showFinalized: type === ScheduleType.Maintenance,
        showNotProceeding: false,
        showActive: true,
        isHandoverDateExist: type === ScheduleType.Maintenance ? true : void 0,
        searchString: query.filter,
        scheduleNotExist: type,
        isDrawRuleExist: type === ScheduleType.Construction ? true : void 0,
        isAuthToCommenceExist: type === ScheduleType.Construction ? true : void 0,
      })
      .select(query).pipe(
        map$(getSlicedData),
        map$((jobs) => jobs.map((job) => ({
          id: job.id,
          name: job.jobNumber,
          description: job.siteAddress,
          display: compact([job.jobNumber, job.contactName, job.siteAddress]).join(' '),
        }))));
  }

  findAllSalesFolders(query: FilteredDataQuery<string>): Observable<AutocompleteItem[]> {
    return this.salesFolderRest.findAllAsAutocompleteDataSource(query.filter)
      .select(query).pipe(
        map$(getSlicedData));
  }

  deliverJobProgressReport(filter: ScheduleJobProgressFilter): Observable<void> {
    return this.constructionScheduleRest
      .deliverJobProgressReport(scheduleJobProgressFilterToDto(filter));
  }

  findAllSupervisors(query: FilteredDataQuery<string>): Observable<ScheduleSupervisorListItem[]> {
    return this.userRest.findAllAsDataSource({
      autocomplete: true,
      showInactive: false,
      systemRoles: systemRolesBitSet(SystemRoleBit.Supervisor),
      name: query.filter,
    }).select(query).pipe(map$(getSlicedData));
  }

  findAllSupervisorTasksAsDataSource(filter: ScheduleSupervisorReportFilter,
                                     printMode?: SchedulesSupervisorPrintMode): SlicedDataSource<ScheduleSupervisorReportTask> {
    const dataSource = this.constructionScheduleRest.findAllSupervisorTasksAsDataSource(
      scheduleSupervisorReportFilterToDto(filter, printMode));
    return decorateDataSourceQuery(dataSource, (query) => ({
      ...query,
      sorting: { field: filter.orderBy, direction: SortDirection.Ascending },
    }));
  }

  findSupervisors(): Observable<UserListItemDto[]> {
    return this.userRest.findSupervisors();
  }

  submitSupervisorScheduleReport(input: SupervsiorReportRequestInput): Observable<void> {
    return this.constructionScheduleRest.submitSupervisorScheduleReport(createSupervisorReportRequestDto(input));
  }

  getJobProgressReportConfig(): Observable<JobProgressReportConfig> {
    return this.constructionScheduleRest.getJobProgressReportConfig();
  }

  findAllVendorsAsDataSource(query: FilteredDataQuery<string>, excludeIds: ID[]): Observable<ContactVendorListItem[]> {
    return this.contactRest.findAllVendorsAsDataSource(query, excludeIds)
      .select(query).pipe(map$(getSlicedData));
  }

  findAllSupervisorsAsDataSource(query: FilteredDataQuery<UserAutocompleteQuery>): Observable<UserAutocompleteDto[]> {
    return this.userRest.getAutocompleteUser(query);
  }

  deliverScheduleVendorReport(report: ScheduleVendorReportRequest): Observable<void> {
    return this.constructionScheduleRest.deliverScheduleVendorReport(vendorReportRequestToDto(report));
  }

  getProduceStatus(task: number): Observable<ReportStatus> {
    return this.constructionScheduleRest.getProduceStatus(task);
  }

  gets3Link(task: number): Observable<any> {
    return this.constructionScheduleRest.getS3Link(task);
  }

  init(jobId: ID, templateId: ID, scheduleType?: ScheduleType): Observable<ScheduleDetailsDto> {
    if (scheduleType === ScheduleType.Construction) {
      return this.constructionScheduleRest.init(jobId, templateId);
    } else if (scheduleType === ScheduleType.PreConstruction) {
      return this.preConstructionScheduleRest.init(jobId, templateId);
    } else if (scheduleType === ScheduleType.Maintenance) {
      return this.maintenanceScheduleRest.init(jobId, templateId);
    } else if (scheduleType === ScheduleType.Sales) {
      return this.salesScheduleRest.init(jobId, templateId);
    } else {
      throw new Error('Schedule type not specified for init method. Unable to determine which REST service to use.');
    }
  }
}
